import axios from "axios";
import store from "../store";
import { setAuth, logout } from "../store/actions";
import { API_BASE_URL, CLIENT_ID } from "./config";

// How long before expiry to proactively refresh.
const REFRESH_MARGIN_MS = 60 * 1000;

let refreshTimer = null;
// Dedupes concurrent refresh attempts (proactive timer + one or more 401s
// racing in) into a single in-flight request that everyone awaits.
let refreshPromise = null;

export function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// Uses raw axios (not the app's `api` instance from utils/common/axios.js)
// so this call is never itself intercepted/retried on a 401 - same pattern
// already used for the authorization_code exchange in Callback.jsx.
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const { refreshToken } = store.getState().auth || {};
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
  });

  refreshPromise = axios
    .post(`${API_BASE_URL}/oauth2/token`, body.toString(), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
    .then((res) => {
      const accessToken = res.data.access_token;
      const expiresIn = res.data.expires_in;
      // Backend may not rotate the refresh token on every call - keep the
      // existing one when it doesn't send a new one.
      const newRefreshToken = res.data.refresh_token || refreshToken;
      const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;

      store.dispatch(
        setAuth({
          accessToken,
          refreshToken: newRefreshToken,
          expiresAt,
        }),
      );

      scheduleRefresh(expiresAt);
      return accessToken;
    })
    .catch((err) => {
      clearRefreshTimer();
      store.dispatch(logout());
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Schedules a proactive refresh REFRESH_MARGIN_MS before expiresAt. Safe to
// call repeatedly (e.g. after every login/refresh, or on app start with a
// rehydrated store) - always clears any previously scheduled timer first.
export function scheduleRefresh(expiresAt) {
  clearRefreshTimer();

  const { refreshToken } = store.getState().auth || {};
  if (!refreshToken || !expiresAt) return;

  const delay = expiresAt - Date.now() - REFRESH_MARGIN_MS;

  if (delay <= 0) {
    refreshAccessToken().catch(() => {});
    return;
  }

  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {});
  }, delay);
}

// Call once on app start so a page reload picks up the refresh schedule for
// tokens rehydrated from localStorage by src/store/index.js.
export function initTokenRefresh() {
  const { expiresAt } = store.getState().auth || {};
  scheduleRefresh(expiresAt);
}
